import agwaSearchStyles from './agwa-search.styles.css?inline';
import { LitElement, html, nothing, unsafeCSS } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import type { SlInput } from '@shoelace-style/shoelace';
// (No directives currently required)

import '@shoelace-style/shoelace/dist/components/input/input.js';
import '@shoelace-style/shoelace/dist/components/icon/icon.js';
import '@shoelace-style/shoelace/dist/components/button/button.js';

/**
 * <agwa-search>
 * Reusable pill-styled search input wrapping Shoelace <sl-input>.
 *
 * Features:
 *  - "pill" large style matching modules hub aesthetic
 *  - prefix search icon
 *  - optional clear button (inside or external) via clearable property
 *  - debounced input events (debounce-ms attribute / debounceMs prop)
 *  - emits custom events:
 *      search-input  -> as user types (debounced)
 *      search-change -> on commit (Enter or clear)
 *      search-clear  -> when cleared
 *  - keyboard focus shortcut suggestion (host can manage global '/')
 *
 * Attributes/Properties:
 *  value (string)          Current search text (reflected)
 *  placeholder (string)    Placeholder text
 *  debounceMs (number)     Milliseconds to debounce input (default 120)
 *  clearable (boolean)     Show built-in clear button (default true)
 *  size ("small"|"medium"|"large")  Pass-through to sl-input (default large)
 *
 * Styling Parts:
 *  --agwa-search-max-width    (CSS var) constrain width (default 360px)
 */
@customElement('agwa-search')
export class AgwaSearch extends LitElement {
  @property({ type: String }) value = '';
  @property({ type: String }) placeholder = 'Search...';
  @property({ type: Number, attribute: 'debounce-ms' }) debounceMs = 120;
  @property({ type: Boolean, reflect: true }) clearable = true;
  /**
   * Small by default. The old default was large, which left the AGWA Staff and Companies
   * searches towering over the small selects and buttons beside them ("some search controls
   * are massive", architect, 2026-09-06); every list bar now shares one control size.
   */
  @property({ type: String }) size: 'small' | 'medium' | 'large' = 'small';

  /** Autofocus when first updated (optional pattern) */
  @property({ type: Boolean }) autofocus = false;

  @state() private _internal = this.value;
  private _debounceHandle: number | undefined;

  static styles = [unsafeCSS(agwaSearchStyles)];

  willUpdate(changed: Map<string, unknown>) {
    if (changed.has('value') && this.value !== this._internal) {
      this._internal = this.value;
    }
  }

  private _queueInputDispatch() {
    if (this._debounceHandle) window.clearTimeout(this._debounceHandle);
    this._debounceHandle = window.setTimeout(() => {
      this.dispatchEvent(
        new CustomEvent('search-input', {
          detail: { value: this._internal },
          bubbles: true,
          composed: true,
        })
      );
    }, this.debounceMs);
  }

  private _onInput(e: CustomEvent) {
    const target = e.currentTarget as SlInput;
    this._internal = target.value || '';
    this._queueInputDispatch();
  }

  private _commit() {
    this.value = this._internal;
    this.dispatchEvent(
      new CustomEvent('search-change', {
        detail: { value: this.value },
        bubbles: true,
        composed: true,
      })
    );
  }

  private _onKeyDown(e: KeyboardEvent) {
    if (e.key === 'Enter') {
      this._commit();
    } else if (e.key === 'Escape') {
      if (this._internal) {
        this._clear();
        e.stopPropagation();
        // The input is type="search", so Chromium would also clear it natively on Escape and fire
        // sl-input, queueing a second, debounced search-input '' after the clear already sent.
        e.preventDefault();
      }
    }
  }

  private _clear() {
    this._internal = '';
    this.value = '';
    this.dispatchEvent(
      new CustomEvent('search-clear', { bubbles: true, composed: true })
    );
    this.dispatchEvent(
      new CustomEvent('search-change', {
        detail: { value: '' },
        bubbles: true,
        composed: true,
      })
    );
  }

  firstUpdated() {
    if (this.autofocus) {
      const input = this.renderRoot.querySelector('sl-input');
      input?.focus();
    }
  }

  render() {
    const clearButton =
      this.clearable && this._internal
        ? html`<sl-button
            size="small"
            variant="text"
            @click=${() => this._clear()}
            style="--padding:0 0.4rem;"
            >Clear</sl-button
          >`
        : nothing;
    return html`<!-- type="search": a search box is not a credential field, which is what keeps Safari and password managers from offering saved logins here (FORM-AUTOFILL; data-entry-autofill.md). -->
      <sl-input
        type="search"
        part="input"
        size=${this.size}
        pill
        ?clearable=${this.clearable}
        placeholder=${this.placeholder}
        .value=${this._internal}
        @sl-input=${this._onInput}
        @keydown=${this._onKeyDown}
        @sl-clear=${() => this._clear()}
      >
        <sl-icon name="search" slot="prefix"></sl-icon>
      </sl-input>
      ${clearButton}`;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'agwa-search': AgwaSearch;
  }
}
